import {GRID_MAX_COLUMNS} from '../vocabulary.js';

/**
 * Pure layout-placement helpers for the page form: carousel slot arithmetic, fixed-column
 * wrapping, overlap detection and the model behind the layout map and carousel order list.
 */

/** Carousel contexts store a slide position in the row/column fields, this many slots per row. */
export const CAROUSEL_COLUMNS = 10;
/** Highest carousel position the order input accepts. */
export const MAX_CAROUSEL_POSITION = 100000;
/** Placeholder path of the page being created, which has no path yet. */
export const PREVIEW_PAGE_PATH = '__preview__';
const LAYOUT_MAP_MAX_COLUMNS = 20;
const LAYOUT_MAP_MAX_ROWS = 60;
const LAYOUT_MAP_MIN_COLUMNS = 2;
const LAYOUT_MAP_MIN_ROWS = 4;

/**
 * @typedef {Object} Placement
 * @property {string} path
 * @property {string} [title]
 * @property {number} row
 * @property {number} column
 * @property {number} width
 * @property {number} height
 * @property {boolean} [pinned]
 * @property {boolean} [staged]
 * @property {string | null} [thumbnailUrl]
 */

/**
 * One-based carousel position of a row/column pair.
 * @param {number} row
 * @param {number} column
 * @returns {number}
 */
export function carouselPosition(row, column) {
    return (row - 1) * CAROUSEL_COLUMNS + column;
}

/**
 * Row/column pair that stores a one-based carousel position.
 * @param {number} position
 * @returns {{row: number, column: number}}
 */
export function carouselSlot(position) {
    return {
        row: Math.floor((position - 1) / CAROUSEL_COLUMNS) + 1,
        column: (position - 1) % CAROUSEL_COLUMNS + 1,
    };
}

/**
 * Position after the last slide of the other pages, capped at {@link MAX_CAROUSEL_POSITION}.
 * @param {import('../../shell/types/user.types.js').TemplateLayoutEntry[]} entries
 * @param {string} pagePath The page being edited, which does not count as another slide
 * @returns {number}
 */
export function nextCarouselPosition(entries, pagePath) {
    const lastSlot = Math.max(0, ...(entries || [])
        .filter((entry) => entry.path !== pagePath && Number.isInteger(entry.row) && Number.isInteger(entry.column))
        .map((entry) => carouselPosition(entry.row, entry.column)));
    return Math.min(lastSlot + 1, MAX_CAROUSEL_POSITION);
}

/**
 * Fixed column count of a layout context, or null for carousels and unbounded grids.
 * @param {import('../../shell/types/user.types.js').TemplateLayoutContextConfig | undefined} contextConfig
 * @param {boolean} carousel
 * @returns {number | null}
 */
export function fixedColumnCount(contextConfig, carousel) {
    if (carousel) return null;
    const columns = Number(contextConfig?.columns);
    return Number.isInteger(columns) && columns >= 1 && columns <= GRID_MAX_COLUMNS ? columns : null;
}

/**
 * Wraps a saved position beyond a fixed column count onto the next rows, as the backend does.
 * @param {number} row
 * @param {number} column
 * @param {number} columns
 * @returns {{row: number, column: number} | null} null when the position needs no wrapping
 */
export function wrapGridPosition(row, column, columns) {
    if (!Number.isInteger(row) || row < 1 || !Number.isInteger(column) || column <= columns) return null;
    return {
        row: row + Math.floor((column - 1) / columns),
        column: (column - 1) % columns + 1,
    };
}

/**
 * @param {Placement} a
 * @param {Placement} b
 * @returns {boolean} whether the two rectangles share a cell
 */
export function placementsOverlap(a, b) {
    const aColEnd = a.column + a.width - 1;
    const aRowEnd = a.row + a.height - 1;
    const bColEnd = b.column + b.width - 1;
    const bRowEnd = b.row + b.height - 1;
    return !(aColEnd < b.column || bColEnd < a.column || aRowEnd < b.row || bRowEnd < a.row);
}

/**
 * Entries of a context with a usable position, as placements with a width and height of at least 1.
 * @param {Array<import('../../shell/types/user.types.js').TemplateLayoutEntry & {thumbnailUrl?: string}>} entries
 * @returns {Placement[]}
 */
function toPlacements(entries) {
    return (entries || [])
        .filter((entry) => entry && entry.path && Number.isInteger(entry.row) && Number.isInteger(entry.column))
        .map((entry) => ({
            path: entry.path,
            title: entry.title,
            row: entry.row,
            column: entry.column,
            width: Number.isInteger(entry.width) && entry.width > 0 ? entry.width : 1,
            height: Number.isInteger(entry.height) && entry.height > 0 ? entry.height : 1,
            pinned: Boolean(entry.pinned),
            staged: Boolean(entry.staged),
            thumbnailUrl: entry.thumbnailUrl || null,
        }));
}

/**
 * @typedef {Object} LayoutMapModel
 * @property {Placement[]} existing Other pages' placements
 * @property {Placement | null} current The page's placement: the server-resolved one when a preview has it
 * @property {boolean} currentWasRelocated The server moved the typed placement
 * @property {boolean} applyRelocation The typed inputs should adopt the relocated position
 * @property {Set<string>} conflictPaths
 * @property {string[]} localConflictPaths Other pages overlapping the current placement
 * @property {Set<string>} hardConflictPaths Pinned pages the server could not move
 * @property {Set<string>} movedPaths Visible pages the server moved
 * @property {Set<string>} otherMovedPaths Visible moved pages other than the page itself
 * @property {number} columns Rendered column count
 * @property {number} rows Rendered row count
 * @property {boolean} currentOutsideMap The current placement does not fit the rendered grid
 * @property {(placement: Placement) => boolean} fits
 */

/**
 * Everything the layout map shows for one context: the pages placed from the server preview
 * (or the stored entries without one), the page's own placement, conflicts, moved pages and
 * the rendered grid size.
 *
 * @param {{
 *   entries: import('../../shell/types/user.types.js').TemplateLayoutEntry[],
 *   preview: import('../../shell/types/user.types.js').LayoutPreviewResponse | null,
 *   currentPath: string,
 *   typed: Placement | null,
 *   lastRequest: {path: string, row: number, column: number, width: number, height: number} | null | undefined,
 *   fixedColumns: number | null,
 * }} input
 * @returns {LayoutMapModel}
 */
export function layoutMapModel({entries, preview, currentPath, typed, lastRequest, fixedColumns}) {
    const hasPreviewAfter = Array.isArray(preview?.after);
    const previewAfter = hasPreviewAfter ? preview.after : null;
    const hardConflictPaths = new Set(Array.isArray(preview?.hardConflicts) ? preview.hardConflicts : []);
    const reportedMovedPaths = new Set(Array.isArray(preview?.movedPaths) ? preview.movedPaths : []);
    const placements = toPlacements(hasPreviewAfter ? previewAfter : entries);
    const visiblePaths = new Set(placements.map((entry) => entry.path));
    const movedPaths = new Set([...reportedMovedPaths].filter((path) => visiblePaths.has(path)));
    const otherMovedPaths = new Set([...movedPaths].filter((path) => path !== currentPath));

    let current = null;
    if (typed && previewAfter) {
        const matched = placements.find((entry) => entry.path === currentPath);
        current = matched || typed;
    } else {
        current = typed;
    }
    const currentWasRelocated = Boolean(
        typed && current
        && (
            typed.row !== current.row
            || typed.column !== current.column
            || typed.width !== current.width
            || typed.height !== current.height
        )
    );

    const requestStillMatchesTyped = Boolean(
        !lastRequest
        || !typed
        || (
            lastRequest.path === currentPath
            && lastRequest.row === typed.row
            && lastRequest.column === typed.column
            && lastRequest.width === typed.width
            && lastRequest.height === typed.height
        )
    );

    const existing = placements.filter((entry) => !current || entry.path !== current.path);
    const localConflictPaths = current
        ? existing.filter((placement) => placementsOverlap(current, placement)).map((placement) => placement.path)
        : [];
    const conflictPaths = new Set([...localConflictPaths, ...hardConflictPaths]);

    const maxColumn = fixedColumns ?? Math.max(
        LAYOUT_MAP_MIN_COLUMNS,
        ...existing.map((entry) => entry.column + entry.width - 1),
        ...(current ? [current.column + current.width - 1] : [])
    );
    const maxRow = Math.max(
        LAYOUT_MAP_MIN_ROWS,
        ...existing.map((entry) => entry.row + entry.height - 1),
        ...(current ? [current.row + current.height - 1] : [])
    );
    const columns = Math.min(maxColumn, LAYOUT_MAP_MAX_COLUMNS);
    const rows = Math.min(maxRow, LAYOUT_MAP_MAX_ROWS);
    const fits = (placement) =>
        placement.column >= 1
        && placement.row >= 1
        && placement.column + placement.width - 1 <= columns
        && placement.row + placement.height - 1 <= rows;

    return {
        existing,
        current,
        currentWasRelocated,
        applyRelocation: currentWasRelocated && requestStillMatchesTyped,
        conflictPaths,
        localConflictPaths,
        hardConflictPaths,
        movedPaths,
        otherMovedPaths,
        columns,
        rows,
        currentOutsideMap: Boolean(current && !fits(current)),
        fits,
    };
}

/**
 * Slides of a carousel context in position order, including the page itself at its typed
 * position. Without a server preview the typed position replaces the page's stored one.
 *
 * @param {{
 *   entries: import('../../shell/types/user.types.js').TemplateLayoutEntry[],
 *   preview: import('../../shell/types/user.types.js').LayoutPreviewResponse | null | undefined,
 *   currentPath: string,
 *   currentTitle: string,
 *   row: number | null,
 *   column: number | null,
 * }} input
 * @returns {Array<{path: string, title?: string, row: number, column: number}>}
 */
export function carouselOrder({entries, preview, currentPath, currentTitle, row, column}) {
    const hasPreviewAfter = Array.isArray(preview?.after);
    const source = hasPreviewAfter ? preview.after : entries || [];
    const slides = source
        .filter((entry) => entry?.path && Number.isInteger(entry.row) && Number.isInteger(entry.column))
        .map((entry) => ({...entry}));
    if (row && column) {
        const current = slides.find((entry) => entry.path === currentPath);
        if (current && !hasPreviewAfter) {
            current.row = row;
            current.column = column;
        } else if (!current) {
            slides.push({path: currentPath, title: currentTitle, row, column});
        }
    }
    return slides.sort((a, b) => a.row - b.row || a.column - b.column
        || (a.path === currentPath ? -1 : b.path === currentPath ? 1 : a.path.localeCompare(b.path)));
}
